import React from 'react';

import {useGate, useUnit} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {generatePath, Redirect, Route, Switch, useLocation, useParams} from 'react-router-dom';

import {PageHeader} from '@/widgets/PageHeader';
import {ROUTES} from '@app/routing';
import {colors} from '@app/styles/theme';
import {EMPTY_VALUE} from '@consts/common';
import {TaxpayerDetailsGate, taxpayerDetailsModel} from '@entities/taxpayers/detail-model';
import {Icon} from '@shared/ui/Icon';
import {Link} from '@shared/ui/Link';
import {Page} from '@shared/ui/Page';
import {StatusWrapper} from '@shared/ui/PageComponents';
import {RouterTab, RouterTabList} from '@shared/ui/RouterTabs';

import {HeaderCard, HeaderTop, TabsContentCard, TaxpayerTitle, Wrapper} from './styled';
import {AccountTab} from './ui/AccountTab';
import {ContractorsTab} from './ui/ContractorsTab';
import {DeclarationTab} from './ui/DeclarationTab';
import {statusColor} from './ui/helpers';
import {InvoicesTab} from './ui/InvoicesTab';
import {MainTab} from './ui/MainTab';
import {NotificationsTab} from './ui/NotificationsTab';
import {ViolationsTab} from './ui/ViolationsTab';

// const gapTypeLabel = (gapType?: string | null) => {
//     if (gapType === 'DECLARATION_NOT_FILED') return 'terms.declaration.gap_type.dec_not_filled';
//     if (gapType === 'NDS_NOT_PAID') return 'terms.declaration.gap_type.tax_not_paid';
//     if (gapType === 'INVOICE_DIFFER') return 'terms.declaration.gap_type.invoice_differ';
//     return 'terms.declaration.gap_type.no_gap';
// };

interface TaxpayerDetailsPageProps {
    basePath?: string;
    backTo?: string;
    titleKey?: string;
    hidePageHeader?: boolean;
}

export const TaxpayerDetailsPage = ({
    basePath = ROUTES.TAXPAYER_DETAILS,
    backTo = ROUTES.TAXPAYERS,
    titleKey = 'page_taxpayers.title',
    hidePageHeader = false,
}: TaxpayerDetailsPageProps) => {
    const {tin} = useParams<{tin: string}>();
    const location = useLocation();
    const {t} = useTranslation();
    const taxpayer = useUnit(taxpayerDetailsModel);
    const info = taxpayer.state.info;
    const path = basePath;
    const url = generatePath(basePath, {tin});

    useGate(TaxpayerDetailsGate, {tin, isMainTab: location.pathname === url});

    return (
        <Page>
            <Wrapper>
                {!hidePageHeader && <PageHeader title={t(titleKey)} />}

                <HeaderCard>
                    <HeaderTop>
                        <TaxpayerTitle>
                            <Link
                                to={backTo}
                                style={{
                                    display: 'flex',
                                    gap: 10,
                                    alignItems: 'center',
                                    marginBottom: 12,
                                }}
                            >
                                <Icon name="arrowLeft" size={16} color={colors.brandPrimary} />
                                {t('actions.back')}
                            </Link>
                            <h2>{info?.name ?? EMPTY_VALUE}</h2>
                            <span>
                                {t('terms.tin')}: {tin}
                            </span>
                        </TaxpayerTitle>
                        <div>
                            <StatusWrapper color={statusColor(info?.liquidated)}>
                                {info?.liquidated == null
                                    ? t('no_data')
                                    : t(
                                          info.liquidated
                                              ? 'terms.taxpayer.liquidated'
                                              : 'terms.taxpayer.not_liquidated',
                                      )}
                            </StatusWrapper>
                            {/*<StatusWrapper color={riskColor(info?.surColour)}>*/}
                            {/*    {info?.surColour*/}
                            {/*        ? t(`terms.declaration.sur.${info.surColour.toLowerCase()}`)*/}
                            {/*        : t('no_data')}*/}
                            {/*</StatusWrapper>*/}
                            {/*<StatusWrapper color={STATUS_COLOR_LIST.YELLOW}>*/}
                            {/*    {t(gapTypeLabel(info?.gapType))}*/}
                            {/*</StatusWrapper>*/}
                        </div>
                    </HeaderTop>
                </HeaderCard>

                <TabsContentCard>
                    <RouterTabList baseUrl={url}>
                        <RouterTab url="">{t('page_taxpayer.tabs.main')}</RouterTab>
                        <RouterTab url="contractors">
                            {t('page_taxpayer.tabs.contractors')}
                        </RouterTab>
                        <RouterTab url="invoices">{t('page_taxpayer.tabs.invoices')}</RouterTab>
                        <RouterTab url="account">{t('page_taxpayer.tabs.balance')}</RouterTab>
                        <RouterTab url="declaration">
                            {t('page_taxpayer.tabs.declaration')}
                        </RouterTab>
                        <RouterTab url="violations">{t('page_taxpayer.tabs.violations')}</RouterTab>
                        <RouterTab url="notifications">
                            {t('page_taxpayer.tabs.notifications')}
                        </RouterTab>
                    </RouterTabList>

                    <Switch>
                        <Route path={path} exact>
                            <MainTab />
                        </Route>
                        <Route path={`${path}/contractors`} exact>
                            <ContractorsTab />
                        </Route>
                        <Route path={`${path}/invoices`} exact>
                            <InvoicesTab />
                        </Route>
                        <Route path={`${path}/account`} exact>
                            <AccountTab />
                        </Route>
                        <Route path={`${path}/declaration`} exact>
                            <DeclarationTab />
                        </Route>
                        <Route path={`${path}/violations`} exact>
                            <ViolationsTab />
                        </Route>
                        <Route path={`${path}/notifications`} exact>
                            <NotificationsTab />
                        </Route>
                        <Route path="*">
                            <Redirect to={url} />
                        </Route>
                    </Switch>
                </TabsContentCard>
            </Wrapper>
        </Page>
    );
};
